// VOID Conflict / UCG — OCC Cloud Save Client Sync Manager
// Synchronizes Arsenal, Custom Decks, Wallet, Booster Packs, and Progression across devices.

import { storageAdapter } from '../engine/storageAdapter.js';
import { inventory } from '../engine/inventory.js';
import { wallet } from '../engine/wallet.js';
import { progress } from '../engine/progress.js';
import { directives } from '../engine/directives.js';
import { loadCustomDecks, saveCustomDecks, reloadCustomDecks, addDeckListener } from '../data/customDecks.js';
import { getAuth, addIdentityListener, handleUnauthorizedIdentity } from './playerIdentity.js';

const STORAGE_KEY_SAVE_VERSION = 'ucg_save_version';
let isApplyingSnapshot = false;
let inMemorySaveVersion = 0;

export function getLocalSaveVersion() {
  try {
    if (typeof localStorage !== 'undefined') {
      const raw = localStorage.getItem(STORAGE_KEY_SAVE_VERSION);
      return raw ? Number(raw) : inMemorySaveVersion;
    }
  } catch {}
  return inMemorySaveVersion;
}

export function setLocalSaveVersion(v) {
  inMemorySaveVersion = Number(v) || 0;
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(STORAGE_KEY_SAVE_VERSION, String(v));
    }
  } catch {}
}

export function buildSnapshot() {
  const invState = storageAdapter.get('inventory', null);
  const walletState = storageAdapter.get('wallet', null);
  const progressState = storageAdapter.get('progress', null);
  const directives = storageAdapter.get('daily_directives', null);
  const streak = storageAdapter.get('daily_streak', null);
  const customDecks = loadCustomDecks();

  return {
    version: 1,
    inventory: invState,
    wallet: walletState,
    customDecks: Array.isArray(customDecks) ? customDecks : [],
    progress: progressState,
    directives: {
      active: directives,
      streak: streak,
    },
  };
}

export function applySnapshot(saveData, saveVersion = null) {
  if (!saveData || typeof saveData !== 'object') return false;

  isApplyingSnapshot = true;
  try {
    if (saveData.inventory && typeof saveData.inventory === 'object') {
      storageAdapter.set('inventory', saveData.inventory);
      inventory.reload?.();
    }

    if (saveData.wallet && typeof saveData.wallet === 'object') {
      storageAdapter.set('wallet', saveData.wallet);
      wallet.reload?.();
    }

    if (Array.isArray(saveData.customDecks)) {
      saveCustomDecks(saveData.customDecks);
      reloadCustomDecks?.();
    }

    if (saveData.progress && typeof saveData.progress === 'object') {
      storageAdapter.set('progress', saveData.progress);
      progress.reload?.();
    }

    if (saveData.directives?.active) {
      storageAdapter.set('daily_directives', saveData.directives.active);
    }

    if (saveData.directives?.streak) {
      storageAdapter.set('daily_streak', saveData.directives.streak);
    }
    directives.reload?.();

    if (saveVersion != null && Number(saveVersion) > 0) {
      setLocalSaveVersion(saveVersion);
    }

    return true;
  } catch (err) {
    console.warn('[CloudSync] Failed to apply save snapshot:', err);
    return false;
  } finally {
    isApplyingSnapshot = false;
  }
}

export function resetLocalGameState() {
  isApplyingSnapshot = true;
  if (debounceTimer) {
    clearTimeout(debounceTimer);
    debounceTimer = null;
  }
  pushQueued = false;

  try {
    storageAdapter.remove('inventory');
    storageAdapter.remove('wallet');
    storageAdapter.remove('progress');
    storageAdapter.remove('daily_directives');
    storageAdapter.remove('daily_streak');
    saveCustomDecks([]);
    setLocalSaveVersion(0);

    inventory.reload?.();
    wallet.reload?.();
    progress.reload?.();
    directives.reload?.();
    reloadCustomDecks?.();
    return true;
  } catch (err) {
    console.warn('[CloudSync] Failed to reset local game state:', err);
    return false;
  } finally {
    isApplyingSnapshot = false;
  }
}

let isPushing = false;
let pushQueued = false;
let debounceTimer = null;

export async function pushSave() {
  const auth = getAuth();
  if (!auth?.accountId || !auth?.secret || auth.isExpired) return;

  if (isPushing) {
    pushQueued = true;
    return;
  }
  isPushing = true;
  pushQueued = false;

  const currentVer = getLocalSaveVersion();
  const nextVer = Math.max(1, currentVer + 1);
  const snapshot = buildSnapshot();

  try {
    const res = await fetch('api/sync_save.php?action=push', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        accountId: auth.accountId,
        secret: auth.secret,
        version: nextVer,
        data: snapshot,
      }),
      keepalive: true,
    });

    if (res.ok) {
      setLocalSaveVersion(nextVer);
    } else if (res.status === 401) {
      await handleUnauthorizedIdentity();
    } else if (res.status === 409) {
      // Concurrency conflict: server has a newer save from another device
      const conflict = await res.json().catch(() => null);
      if (conflict?.serverData) {
        applySnapshot(conflict.serverData, conflict.serverVersion);
      }
    }
  } catch (err) {
    // Network offline - will be retried on next change or page unload
  } finally {
    isPushing = false;
    if (pushQueued) {
      pushSave();
    }
  }
}

export function schedulePush(delayMs = 2500) {
  if (isApplyingSnapshot) return;
  if (debounceTimer) clearTimeout(debounceTimer);
  debounceTimer = setTimeout(() => {
    debounceTimer = null;
    pushSave();
  }, delayMs);
}

export function flushSync() {
  if (isApplyingSnapshot) return;
  if (debounceTimer) {
    clearTimeout(debounceTimer);
    debounceTimer = null;
  }
  pushSave();
}

export async function pullSave() {
  const auth = getAuth();
  if (!auth?.accountId || !auth?.secret || auth.isExpired) return null;

  try {
    const res = await fetch('api/sync_save.php?action=pull', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        accountId: auth.accountId,
        secret: auth.secret,
      }),
    });

    if (res.status === 401) {
      await handleUnauthorizedIdentity();
      return null;
    }
    if (!res.ok) return null;
    const body = await res.json().catch(() => null);
    if (body?.ok && body.data) {
      const serverVersion = Number(body.version || 0);
      const localVersion = getLocalSaveVersion();

      // Adopt server data if server has a version or if local has never synced
      if (serverVersion > localVersion || localVersion === 0) {
        applySnapshot(body.data, serverVersion);
        return body.data;
      }
    }
  } catch {}
  return null;
}

let initialized = false;
let initialSyncDone = false;

async function triggerInitialSync() {
  if (initialSyncDone) return;
  const auth = getAuth();
  if (!auth?.accountId || !auth?.secret || auth.isExpired) return;
  initialSyncDone = true;

  const localVer = getLocalSaveVersion();
  if (localVer === 0) {
    // Check if server already has a save for this account
    const pulled = await pullSave();
    const currentAuth = getAuth();
    if (!pulled && currentAuth?.accountId && currentAuth?.secret && !currentAuth.isExpired) {
      // Fresh account or empty server: push initial local state
      pushSave();
    }
  } else {
    // Check for updates from another device
    pullSave();
  }
}

export function initCloudSync() {
  if (initialized) return;
  initialized = true;

  // 1. Auto-save triggers on state modifications
  inventory.addListener(() => schedulePush());
  wallet.addListener(() => schedulePush());
  progress.addListener(() => schedulePush());
  directives.addListener?.(() => schedulePush());
  addDeckListener(() => schedulePush());

  // 2. Lifecycle triggers for mobile/tab switches
  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') flushSync();
    });
  }
  if (typeof window !== 'undefined') {
    window.addEventListener('pagehide', () => flushSync());
    window.addEventListener('beforeunload', () => flushSync());
  }

  // 3. Initial sync check: if already authenticated or on identity adoption
  triggerInitialSync();
  addIdentityListener((auth) => {
    if (auth?.accountId && auth?.secret) {
      triggerInitialSync();
    }
  });
}
